import {
  CREATE_RADIO,
  DELETE_RADIO,
  FETCH_RADIO,
  FETCH_RADIO_SUCCESS,
  REMOVE_RADIO,
  UPDATE_RADIO,
} from '@/actions';
import { Section } from '@/components/controls';
import { ConfirmationDialog } from '@/components/dialogs';
import {
  SelectField,
  StyledField,
  TextField,
  required,
} from '@/components/fields';
import { useAuth, useDocumentTitle } from '@/hooks';
import { doesIdExist } from '@/utils';
import { radioTypes } from '@/utils/config';
import {
  Person as PersonIcon,
  RemoveCircle as RemoveCircleIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardHeader,
  Divider,
  IconButton,
  ListSubheader,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { dequal } from 'dequal';
import { RadioHandheld as RadioHandheldIcon } from 'mdi-material-ui';
import { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';

export function Radio() {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const radio = useSelector((state) => state.radios.radio, dequal);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [removePerson, setRemovePerson] = useState(null);
  const auth = useAuth();
  const canEdit = auth.isAuthorised('people', true);
  useDocumentTitle(`IR3 | Radio${radio?.ssi ? ` | ${radio?.ssi}` : ''}`);

  useEffect(() => {
    if (id === 'new') {
      dispatch({
        type: FETCH_RADIO_SUCCESS,
        payload: null,
      });
    } else {
      dispatch({
        type: FETCH_RADIO,
        payload: id,
      });
    }
  }, [id, dispatch]);

  async function onSubmit(values) {
    if (radio) {
      dispatch({
        type: UPDATE_RADIO,
        payload: values,
      });
    } else {
      const exists = await doesIdExist('radios', values.ssi);
      if (exists) {
        return { ssi: 'Exists' };
      }

      dispatch({
        type: CREATE_RADIO,
        payload: values,
        navigate,
      });
    }
  }

  function handleDelete() {
    if (radio) {
      dispatch({
        type: DELETE_RADIO,
        payload: radio.ssi,
        navigate,
      });
    }
  }

  function isDisabled() {
    return !canEdit || radio === null;
  }

  const handleRemoveClick = (person) => (event) => {
    event.preventDefault();

    setRemovePerson(person);
  };

  function handleRemove() {
    const body = {
      code: removePerson.code,
      radioSsi: null,
    };

    dispatch({
      type: REMOVE_RADIO,
      payload: { id, body },
    });

    setRemovePerson(null);
  }

  return (
    <Box sx={{ flexGrow: 1, p: 1, bgcolor: 'background.paper' }}>
      <Form
        initialValues={radio || {}}
        onSubmit={onSubmit}
        render={({
          handleSubmit,
          form: { reset },
          submitting,
          pristine,
          values,
        }) => (
          <form onSubmit={handleSubmit}>
            <Stack spacing={1}>
              <CardHeader
                avatar={
                  <Avatar aria-label="Radio">
                    <RadioHandheldIcon />
                  </Avatar>
                }
                title={values.ssi}
                subheader={values.type}
              />
              <Box>
                <ListSubheader disableSticky>Key Information</ListSubheader>
                <Section>
                  <StyledField
                    name="ssi"
                    component={TextField}
                    label="SSI"
                    validate={required}
                    disabled={!(canEdit && radio === null)}
                  />
                  <StyledField
                    name="type"
                    component={SelectField}
                    label="Type"
                    values={radioTypes}
                    disabled={isDisabled('type')}
                  />
                  <StyledField
                    name="label"
                    component={TextField}
                    label="Label"
                    disabled={isDisabled('label')}
                  />
                </Section>
                <Divider />
                <ListSubheader disableSticky>Assigned People</ListSubheader>
                <Section>
                  {radio && radio.people.length > 0 ? (
                    radio.people.map((person) => (
                      <Card
                        key={person.code}
                        variant="outlined"
                        sx={{ width: 320, mr: 1, mb: 1 }}
                      >
                        <CardActionArea
                          component={Link}
                          to={`/resources/people/${encodeURIComponent(
                            person.code,
                          )}`}
                        >
                          <CardHeader
                            avatar={
                              <Avatar src={person.picture}>
                                <PersonIcon />
                              </Avatar>
                            }
                            action={
                              <Tooltip title="Remove">
                                <IconButton
                                  onClick={handleRemoveClick(person)}
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                  }}
                                >
                                  <RemoveCircleIcon />
                                </IconButton>
                              </Tooltip>
                            }
                            title={`${person.forenames} ${person.surname}`}
                            subheader={person.collarNumber}
                          />
                        </CardActionArea>
                      </Card>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      There is no one assigned to this radio
                    </Typography>
                  )}
                </Section>
              </Box>
              <CardActions>
                <Button
                  color="primary"
                  type="submit"
                  disabled={pristine || submitting}
                >
                  Save
                </Button>
                <Button
                  color="primary"
                  disabled={pristine || submitting}
                  onClick={reset}
                >
                  Cancel
                </Button>
                <Button
                  color="error"
                  onClick={() => setDeleteOpen(true)}
                  disabled={radio === null}
                >
                  Delete
                </Button>
              </CardActions>
            </Stack>
            <ConfirmationDialog
              action="Delete"
              open={deleteOpen}
              itemId={values.ssi}
              onOk={handleDelete}
              onCancel={() => setDeleteOpen(false)}
            />

            <ConfirmationDialog
              action="Remove"
              open={!!removePerson}
              itemId={
                removePerson
                  ? `${removePerson.forenames || ''} ${
                      removePerson.surname || ''
                    }` ||
                    removePerson.collarNumber ||
                    removePerson.code
                  : ''
              }
              onOk={handleRemove}
              onCancel={() => setRemovePerson(null)}
            />
          </form>
        )}
      />
    </Box>
  );
}
